<script setup>
import { computed, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    buttonText: {
        type: String,
        default: '무한'
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isChecked = ref(props.checked);
const isInfinite = ref(false);
const lastNumericValue = ref('');
const displayValue = ref('');

const onInfiniteChange = () => {
    if (isInfinite.value) {
        inputValue.value = lastNumericValue.value;
    } else {
        inputValue.value = '-1';
    }
    emitUpdate(inputValue.value);
};

onMounted(async () => {});

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    value = value.toString().replace(/[^0-9]/g, '');

    value = value.toString().replace(/\./g, '');

    // 정수 부분과 소수 부분 분리
    const [integerPart, decimalPart] = value.split('.');

    // 정수 부분에 3자리마다 쉼표 추가
    let formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 앞에 불필요한 0 제거
    formattedIntegerPart = formattedIntegerPart.replace(/^0+/, '');
    if (formattedIntegerPart === '') formattedIntegerPart = '0';

    // 결과 조합
    let result = formattedIntegerPart;
    if (decimalPart !== undefined) {
        result += '.' + decimalPart;
    }

    // '0.'으로 시작하는 경우 처리
    if (result.startsWith('.')) {
        result = '0' + result;
    }

    return result;
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        if (newVal === -1) {
            isInfinite.value = true;
            inputValue.value = -1;
        } else {
            isInfinite.value = false;
            inputValue.value = newVal;
        }
    },
    { immediate: true }
);

watch(
    [inputValue, isInfinite],
    ([newInputValue, newIsInfinite]) => {
        displayValue.value = newIsInfinite ? '' : formatNumber(newInputValue);
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    let value = event.target.value;
    value = formatNumber(value);
    lastNumericValue.value = value;
    emitUpdate(value);
};

const emitUpdate = (value) => {
    // if (typeof value !== 'string') {
    //     value = String(value);
    // }
    value = value.toString().replaceAll(',', '');
    inputValue.value = value;
    displayValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};

const clearInput = () => {
    inputValue.value = '';
    lastNumericValue.value = '';
    emitUpdate('', 'input');
};
</script>

<template>
    <div
        v-if="visible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label select-none">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>
        <div class="relative flex items-center w-full h-12">
            <input
                type="text"
                :id="inputId"
                v-model="displayValue"
                class="p-inputtext block w-full ps-5 h-[2.5rem] input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'text-right': !isInfinite,
                    'error-border': ValidationMsg,
                    'readonly-input': isInfinite,
                    'readonly-Bg': isReadonly
                }"
                :readonly="isInfinite || isReadonly"
                required
                :placeholder="isInfinite ? buttonText : placeholder"
                :required="required"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <span v-if="!isInfinite" class="unit-text">만원</span>
                <button v-if="inputValue && !isReadonly && !isInfinite" @click="clearInput" class="clear-button mr-[calc(5.5rem)]">&times;</button>
                <input type="checkbox" id="checkId" class="checkbox-input" :disabled="isReadonly" v-model="isInfinite" @click="onInfiniteChange" />
                <label class="checkbox-label select-none"> {{ isInfinite ? '' : buttonText }} </label>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.readonly-input {
    background-color: #f3f4f6;
    color: #9ca3af;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 10rem;
}

.error-border {
    border: 1px solid red !important;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}

.custom-input.p-inputtext {
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.custom-input.p-inputtext:focus:not([readonly]) {
    border-color: #0038ea !important;
}

.checkbox-container {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.checkbox-input {
    width: 1rem;
    height: 1.4rem;
    margin-right: 0.6rem;
    border-radius: 0.25rem;
    cursor: pointer;
}

.checkbox-label {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    margin-right: 1rem;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    position: absolute;
    right: 7.5rem;
    display: flex;
    align-items: center;
    height: 100%;
    font-family: 'SCoreDream500';
    font-size: 0.875rem;
    color: #6b7280;
    user-select: none;
    white-space: nowrap;
}
.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.875rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

input::-webkit-input-placeholder {
    text-align: left !important;
}
input::-moz-placeholder {
    /* Firefox 19+ */
    text-align: left !important;
}
input:-ms-input-placeholder {
    /* IE 10+ */
    text-align: left !important;
}
input:-moz-placeholder {
    /* Firefox 18- */
    text-align: left !important;
}
</style>
